/**
 * B-24b WS-3 — Per-customer usage trend panel.
 *
 * Wired to GET /admin/sa-dashboard/usage-trend.
 * Shows weekly credit consumption + % of credit allotment per tenant.
 *
 * Low-data rules (IV-L1):
 *   - available = 0 → pct_of_allotment renders "—" (div-by-zero guard)
 *   - Single week of data → bar chart instead of line (< 4 pts)
 *   - Empty tenants array → intentional "no usage data yet" message
 *
 * Tenant-zone only. Does NOT reference platform_rating_signals (IV-Z5).
 */
import ReactECharts from "echarts-for-react";
import { useSaUsageTrend } from "@/hooks/queries/admin/useSaDashboard";
import type { UsageTrendTenant } from "@/lib/saDashboardApi";
import {
  ECHARTS_COLORS,
  ECHARTS_TOOLTIP_BG,
  ECHARTS_TOOLTIP_BORDER,
  ECHARTS_TEAL_DIM,
  ECHARTS_TEAL_AREA_START,
  ECHARTS_TEAL_AREA_END,
  ECHARTS_SPLIT_COLOR,
} from "@/lib/tokens";
import { OperatorPanel } from "./OperatorPanel";

const ACCENT = ECHARTS_COLORS[1];     // --p-teal-500 #00E5CC
const ACCENT_DIM = ECHARTS_TEAL_DIM;  // --p-teal-600 #00C8B8

/** Format consumed credits: e.g. 41200 → "41.2k" */
function fmtCredits(n: number): string {
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

/** Pct of allotment guard: null entitlement (P1-5) or available=0 → "—". */
function fmtPct(pct: number | null, available: number): string {
  if (pct === null || available === 0) return "—";
  return `${Math.round(pct)}%`;
}

function TenantRow({ tenant }: { tenant: UsageTrendTenant }) {
  const weeks = tenant.weekly;
  const latest = weeks[weeks.length - 1];
  const totalConsumed = weeks.reduce((s, w) => s + w.consumed, 0);

  // Use bar for < 4 data points, line otherwise (IV-L1 flatline guard)
  const chartType = weeks.length < 4 ? "bar" : "line";

  const chartOption = {
    textStyle: { fontFamily: "Geist, Inter, sans-serif" },
    grid: { left: 0, right: 0, top: 4, bottom: 4 },
    xAxis: {
      type: "category",
      show: false,
      data: weeks.map((w) => w.week_bucket),
    },
    yAxis: { type: "value", show: false, scale: true },
    series: [
      {
        type: chartType,
        data: weeks.map((w) => w.consumed),
        showSymbol: false,
        smooth: chartType === "line",
        lineStyle: chartType === "line" ? { width: 1.5, color: ACCENT } : undefined,
        itemStyle: chartType === "bar" ? { color: ACCENT_DIM } : undefined,
        areaStyle:
          chartType === "line"
            ? {
                color: {
                  type: "linear",
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: ECHARTS_TEAL_AREA_START },
                    { offset: 1, color: ECHARTS_TEAL_AREA_END },
                  ],
                },
              }
            : undefined,
      },
    ],
    tooltip: {
      trigger: "axis",
      backgroundColor: ECHARTS_TOOLTIP_BG,
      borderColor: ECHARTS_TOOLTIP_BORDER,
      textStyle: { color: ECHARTS_SPLIT_COLOR, fontSize: 11 },
      valueFormatter: (v: number) => fmtCredits(v),
    },
  };

  return (
    <div className="grid grid-cols-[1fr_96px] items-center gap-3 py-2 border-b border-border-default last:border-0">
      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="truncate text-xs font-medium text-text-primary">
            {tenant.name}
          </span>
          {latest && (
            <span className="font-mono text-[11px] tabular-nums text-text-tertiary">
              {fmtCredits(totalConsumed)} consumed
            </span>
          )}
        </div>
        {latest && (
          <div className="mt-0.5 text-[11px] text-text-tertiary">
            latest week:{" "}
            <span className="font-mono tabular-nums">
              {fmtCredits(latest.consumed)}
            </span>
            {" · "}
            <span
              className={
                "font-mono tabular-nums font-semibold " +
                (latest.pct_of_allotment === null || latest.available === 0
                  ? "text-text-tertiary"
                  : latest.pct_of_allotment >= 90
                    ? "text-brand-negative"
                    : latest.pct_of_allotment >= 70
                      ? "text-brand-warn"
                      : "text-brand-positive")
              }
            >
              {fmtPct(latest.pct_of_allotment, latest.available)}
            </span>
            {" of allotment"}
          </div>
        )}
      </div>
      {weeks.length > 0 ? (
        <ReactECharts style={{ height: 36 }} option={chartOption} notMerge />
      ) : (
        <div className="h-9 rounded bg-surface-canvas text-center text-[10px] leading-9 text-text-tertiary">
          no data
        </div>
      )}
    </div>
  );
}

export function UsageTrendPanel() {
  const { data, isLoading, isError } = useSaUsageTrend();

  return (
    <OperatorPanel
      testId="usage-trend-panel"
      kicker="Credit consumption"
      title="Usage trend"
    >
      <div>
        {isLoading && (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-12 animate-pulse rounded bg-surface-canvas"
              />
            ))}
          </div>
        )}
        {isError && (
          <p className="text-xs text-brand-negative">
            Couldn't load usage trends.
          </p>
        )}
        {data && data.tenants.length === 0 && (
          <p
            data-testid="usage-trend-empty"
            className="text-xs text-text-tertiary"
          >
            No usage data yet — first week of data will appear here.
          </p>
        )}
        {data && data.tenants.length > 0 && (
          <div className="max-h-[280px] overflow-y-auto">
            {data.tenants.map((t) => (
              <TenantRow key={t.tenant_id} tenant={t} />
            ))}
          </div>
        )}
      </div>
    </OperatorPanel>
  );
}
